import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronDown, Plus } from 'lucide-react';
import { useAuth } from '../../context/authcontext';
import { usePermission } from '../../hooks/usePermission';
import { createPermissionString as perm } from '../../types/permissions';
import { useToast } from '../common/Toast';
import { resolveAgencyTimezone, toAgencyDate } from '../../utils/agencyTime';
import { ScheduleService, scheduleErrorMessage } from '../../services/scheduling';
import type { UserData } from '../../dtos/admin/user.dto';
import type { Shift } from '../../dtos/scheduling/schedule.dto';
import AgendaList from './AgendaList';
import ShiftDrawer from './ShiftDrawer';
import CancelShiftDialog from './CancelShiftDialog';
import { addDays, dayLabel, formatMinutes, isCountedShift, shiftWindow } from './scheduleUtils';

const DAYS = 14;

export interface PatientScheduleTabProps {
  patientId: string;
  patientName: string;
  /** patients.status, lowercase */
  patientStatus?: string | null;
  expanded: boolean;
  toggleExpanded: () => void;
}

/**
 * Patient chart → Schedule (plan §6.1): the next 14 days for this patient with a prefilled
 * "+ Shift" (patient locked). Held patients can't get new shifts (SCH-04); the banner says why.
 */
const PatientScheduleTab: React.FC<PatientScheduleTabProps> = ({ patientId, patientName, patientStatus, expanded, toggleExpanded }) => {
  const { agencyTimezone } = useAuth();
  const { showToast } = useToast();
  const canWrite = usePermission([perm('scheduling:phi:create'), perm('scheduling:phi:createall')]);
  const today = useMemo(() => toAgencyDate(new Date(), resolveAgencyTimezone(agencyTimezone)), [agencyTimezone]);
  const days = useMemo(() => Array.from({ length: DAYS }, (_, i) => addDays(today, i)), [today]);
  const [shifts, setShifts] = useState<Shift[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [clinicians, setClinicians] = useState<UserData[]>([]);
  const [drawer, setDrawer] = useState<{ open: boolean; shift: Shift | null }>({ open: false, shift: null });
  const [cancelTarget, setCancelTarget] = useState<Shift | null>(null);
  const [busy, setBusy] = useState(false);
  const status = String(patientStatus || '').toLowerCase();
  const blocked = status === 'on_hold' || status === 'inactive';

  const load = useCallback(async () => {
    try {
      setShifts(await ScheduleService.listByPatient(patientId, today, addDays(today, DAYS - 1)));
      setError(null);
    } catch (e) {
      setError(scheduleErrorMessage(e, 'Could not load the schedule'));
    }
  }, [patientId, today]);

  useEffect(() => { if (expanded) load(); }, [expanded, load]);
  useEffect(() => {
    if (expanded && canWrite) ScheduleService.listClinicians().then(setClinicians).catch(() => setClinicians([]));
  }, [expanded, canWrite]);

  const minutes = (shifts || []).filter(isCountedShift).reduce((sum, s) => sum + (s.durationMinutes || 0), 0);
  const unstaffed = (shifts || []).filter((s) => s.flags?.unstaffed).length;

  return (
    <div className="mb-0 md:mb-2">
      <div
        className="flex cursor-pointer items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-4 hover:bg-slate-100 md:hidden"
        onClick={toggleExpanded}
      >
        <h2 className="flex items-center text-lg font-semibold text-slate-800"><CalendarDays size={20} className="mr-3 text-slate-600" />Schedule</h2>
        <ChevronDown size={20} className={`text-slate-600 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </div>
      {expanded && (
        <div className="space-y-3 p-4 md:p-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="hidden text-lg font-semibold text-slate-800 md:block">Schedule · next 2 weeks</h2>
            {shifts && (
              <span className="text-sm text-gray-600">
                {shifts.filter(isCountedShift).length} shifts · {formatMinutes(minutes)}
                {unstaffed > 0 && <span className="ml-2 font-medium text-red-700">{unstaffed} unstaffed</span>}
              </span>
            )}
            {canWrite && (
              <button
                type="button"
                disabled={blocked}
                title={blocked ? 'This patient can\'t be scheduled while on hold or discharged' : 'New shift for this patient'}
                onClick={() => setDrawer({ open: true, shift: null })}
                className="ml-auto flex min-h-[44px] items-center gap-1.5 rounded-md bg-slate-800 px-4 text-sm font-semibold text-white disabled:opacity-50 md:min-h-[36px]"
              >
                <Plus size={16} />Shift
              </button>
            )}
          </div>
          {status === 'on_hold' && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              Patient is on hold: no new shifts. Existing shifts stay until you cancel them (reason "Patient hospitalized" or other).
            </div>
          )}
          {error && <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
          {!shifts && !error && <p className="text-sm text-gray-500">Loading…</p>}
          {shifts && (
            <AgendaList
              days={days}
              shifts={shifts}
              today={today}
              primary="provider"
              onOpen={canWrite ? (s) => setDrawer({ open: true, shift: s }) : undefined}
              emptyText="No shifts in the next 2 weeks."
            />
          )}
        </div>
      )}
      {canWrite && (
        <ShiftDrawer
          open={drawer.open}
          shift={drawer.shift}
          prefill={{ patientId, patientLabel: patientName, date: today }}
          lockPatient
          clinicians={clinicians}
          today={today}
          onClose={() => setDrawer({ open: false, shift: null })}
          onSaved={() => { setDrawer({ open: false, shift: null }); load(); }}
          onCancelShift={setCancelTarget}
        />
      )}
      <CancelShiftDialog
        open={Boolean(cancelTarget)}
        summary={cancelTarget ? `${dayLabel(cancelTarget.date)} ${shiftWindow(cancelTarget, true)}` : ''}
        allowFollowing={Boolean(cancelTarget?.isRecurring)}
        busy={busy}
        onCancel={() => setCancelTarget(null)}
        onConfirm={async (code, note, scope) => {
          if (!cancelTarget) return;
          setBusy(true);
          try {
            await ScheduleService.cancel(cancelTarget.id, code, note, scope);
            showToast('Shift cancelled', { variant: 'success' });
            setCancelTarget(null);
            setDrawer({ open: false, shift: null });
            load();
          } catch (e) {
            showToast(scheduleErrorMessage(e, 'Could not cancel the shift'), { variant: 'error' });
          } finally {
            setBusy(false);
          }
        }}
      />
    </div>
  );
};

export default PatientScheduleTab;
